import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import {
  GraduationCap,
  Award,
  BookOpen,
  FileText,
  ShieldCheck,
  ExternalLink,
  Users,
  Globe,
  FlaskConical,
  Star,
  Image as ImageIcon,
  X,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

function AnimatedSection({ children, delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
}

const activityIcons = [Globe, FlaskConical, Users, Award, BookOpen];

const defaultAwardsData = [
  {
    title: 'Lulusan Terbaik Akademik dan Non-Akademik Periode April 2025',
    issuer: 'Universitas Aisyiyah Yogyakarta',
    year: '2026',
    description: 'Meraih prestasi akademik dengan perolehan IPK 3.4/4.0 dan prestasi non-akademik berupa Kejuaraan Lomba MTQ Tingkat Universitas & Nasional.',
    highlight: true,
    proofImages: ['/assets/Awards/Sertifikat Prestasi Akademik.jpeg', '/assets/Awards/Sertifikat Prestasi non-Akademik.jpeg'],
  },
  {
    title: 'Juara 2 Lomba MTQ Tingkat Nasional',
    issuer: 'Universitas Islam Jakarta',
    year: '2021',
    description: 'Mendapatkan juara 2 dalam lomba Musabaqah Tilawatil Quran (MTQ) tingkat nasional yang diadakan oleh Universitas Islam Jakarta.',
    proofImages: ['/assets/Awards/Sertifikat Juara 2 MTQ UIJ JPG version.jpg'],
  },
  {
    title: 'Juara 1 Lomba MTQ Tingkat Universitas',
    issuer: 'Universitas Aisyiyah Yogyakarta',
    year: '2021',
    description: 'Meraih juara 1 dalam lomba Musabaqah Tilawatil Quran (MTQ) tingkat universitas yang diadakan oleh Universitas Aisyiyah Yogyakarta.',
    proofImages: [
      '/assets/Awards/Juara 1 - Tartil LPTQ Unisa.jpg',
    ],
  },
  {
    title: 'Juara 2 Lomba Pencak Silat Tingkat Kota Jakarta Barat',
    issuer: 'Universitas Aisyiyah Yogyakarta',
    year: '2019',
    description: 'Meraih juara 2 untuk kelas D Putra dalam lomba Pencak Silat tingkat kota Jakarta Barat.',
    proofImages: [
      '/assets/Awards/Restu Agil Yuli Arjun-Sertifikat Juara 2 Pencak Silat.jpg',
    ],
  },
];

// Tambahkan karya publikasi di sini. Cukup isi journalUrl dengan link jurnal/paper.
const defaultPublicationsData = [
  {
    title: 'Deteksi Bahan Pangan Tinggi Protein Menggunakan Model You Only Look Once (YOLO)',
    journal: 'Building of Informatics, Technology and Science (BITS) Journal',
    year: '2025',
    authors: 'Restu Agil Yuli Arjun, Esi Putri Silmina.',
    description: 'Penelitian deteksi bahan MP-ASI berbasis computer vision untuk mendukung intervensi gizi anak secara lebih cepat dan akurat.',
    journalUrl: 'https://ejurnal.seminar-id.com/index.php/bits/article/view/6889/3563',
  },
  {
    title: 'Pemanfaatan Model YOLOv8Untuk Mendeteksi Plat Nomor Kendaraan Mobil Pada Gerbang Masuk Universitas XYZ',
    journal: 'Jurnal Sains dan Informatika',
    year: '2025',
    authors: 'Esi Putri Silmina, Restu Agil Yuli Arjun.',
    description: 'Penelitian deteksi plat nomor kendaraan menggunakan YOLOv8 untuk meningkatkan keamanan dan efisiensi di gerbang masuk kampus.',
    journalUrl: 'https://jsi.politala.ac.id/index.php/JSI/article/view/916/212',
  },
];

const defaultPatentsData = [
  {
    title: 'SIMANIS (Deteksi Menu Makanan Pendamping ASI)',
    institution: 'Direktorat Jenderal Kekayaan Intelektual',
    year: '2024',
    patentNumber: 'EC002024193259',
    status: 'Terdaftar',
    description: 'Inovasi sistem pendukung identifikasi bahan MP-ASI berbasis AI untuk membantu pemantauan gizi pada layanan kesehatan.',
    referenceUrl: 'https://pdki-indonesia.dgip.go.id/detail/e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
  },
  {
    title: 'Penerapan Algoritma Genetika untuk Penentuan Menu Makanan Terbaik dan Terburuk',
    institution: 'Direktorat Jenderal Kekayaan Intelektual',
    year: '2023',
    patentNumber: 'EC00202366511',
    status: 'Terdaftar',
    description: 'Inovasi metode penentuan menu makanan terbaik dan terburuk menggunakan algoritma genetika untuk mendukung pengambilan keputusan dalam perencanaan gizi.',
    referenceUrl: 'https://pdki-indonesia.dgip.go.id/detail/e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
  },
  {
    title: 'Aplikasi Inventaris Obat',
    institution: 'Direktorat Jenderal Kekayaan Intelektual',
    year: '2023',
    patentNumber: 'EC00202317030',
    status: 'Terdaftar',
    description: 'Inovasi aplikasi inventaris obat berbasis AI untuk meningkatkan efisiensi manajemen stok dan distribusi obat di fasilitas kesehatan.',
    referenceUrl: 'https://pdki-indonesia.dgip.go.id/detail/e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
  },
];

function withProtocol(url) {
  if (!url) return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

function getPublicationPreviewUrl(journalUrl) {
  const normalizedUrl = withProtocol(journalUrl);
  if (!normalizedUrl) return '';

  const driveMatch = normalizedUrl.match(/drive\.google\.com\/file\/d\/([^/]+)/i);
  if (driveMatch?.[1]) {
    return `https://drive.google.com/file/d/${driveMatch[1]}/preview`;
  }

  try {
    const parsedUrl = new URL(normalizedUrl);
    const fullPath = `${parsedUrl.pathname}${parsedUrl.search}`.toLowerCase();

    if (fullPath.includes('.pdf')) {
      return parsedUrl.toString();
    }

    // Pola umum Open Journal Systems (OJS): /article/view/{id} -> /article/view/{id}/pdf
    if (parsedUrl.pathname.toLowerCase().includes('/article/view/')) {
      parsedUrl.pathname = parsedUrl.pathname.replace(/\/$/, '') + '/pdf';
      parsedUrl.search = '';
      return parsedUrl.toString();
    }
  } catch {
    return '';
  }

  return `https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(normalizedUrl)}`;
}

const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? '100%' : '-100%',
  }),
  center: {
    x: 0,
  },
  exit: (direction) => ({
    x: direction > 0 ? '-100%' : '100%',
  }),
};

function AwardProofLightbox({ award, onClose, labels }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState(1);

  const images = award?.proofImages || [];
  const hasImages = images.length > 0;
  const hasMultiple = images.length > 1;

  useEffect(() => {
    if (!award || !hasImages) return;

    award.proofImages.forEach((imageSrc) => {
      const img = new Image();
      img.src = imageSrc;
    });
  }, [award, hasImages]);

  if (!award) return null;

  const nextImage = () => {
    setSlideDirection(1);
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setSlideDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleDragEnd = (_, info) => {
    if (!hasMultiple) return;

    const swipeThreshold = 80;
    const velocityThreshold = 500;

    if (info.offset.x < -swipeThreshold || info.velocity.x < -velocityThreshold) {
      nextImage();
      return;
    }

    if (info.offset.x > swipeThreshold || info.velocity.x > velocityThreshold) {
      prevImage();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ type: 'spring', damping: 26, stiffness: 280 }}
        className="relative glass rounded-2xl max-w-5xl w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative w-full h-[72vh] overflow-hidden bg-black">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-black/65 border border-white/25 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/80 transition-colors"
            aria-label={labels.closeProof}
          >
            <X size={20} />
          </button>

          <AnimatePresence initial={false} custom={slideDirection} mode="sync">
            {hasImages ? (
              <motion.img
                key={currentIndex}
                src={images[currentIndex]}
                alt={`${award.title} proof ${currentIndex + 1}`}
                custom={slideDirection}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                drag={hasMultiple ? 'x' : false}
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.12}
                onDragEnd={handleDragEnd}
                transition={{ type: 'tween', duration: 0.28, ease: 'easeInOut' }}
                className="absolute inset-0 w-full h-full object-contain select-none cursor-grab active:cursor-grabbing"
              />
            ) : (
              <div className="h-full flex items-center justify-center bg-linear-to-br from-accent-500/20 to-cyan-500/20">
                <ImageIcon size={64} className="text-accent-400/50" />
              </div>
            )}
          </AnimatePresence>

          {hasMultiple && (
            <>
              <button
                onClick={prevImage}
                className="absolute top-1/2 left-4 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-black/65 border border-white/30 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/80 transition-colors shadow-lg"
                aria-label={labels.previousProof}
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={nextImage}
                className="absolute top-1/2 right-4 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-black/65 border border-white/30 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/80 transition-colors shadow-lg"
                aria-label={labels.nextProof}
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          {hasMultiple && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    if (index === currentIndex) return;
                    setSlideDirection(index > currentIndex ? 1 : -1);
                    setCurrentIndex(index);
                  }}
                  className={`w-2.5 h-2.5 rounded-full transition-colors ${
                    currentIndex === index ? 'bg-accent-500' : 'bg-white/40 hover:bg-white/60'
                  }`}
                  aria-label={`${labels.goToProof} ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="p-6">
          <p className="text-accent-400 text-xs font-mono mb-2">{labels.proofTag}</p>
          <h3 className="text-heading text-xl font-bold mb-1">{award.title}</h3>
          <p className="text-muted text-sm mb-2">{award.issuer}</p>
          <p className="text-faint text-xs">{award.year}</p>
        </div>
      </motion.div>
    </motion.div>
  );
}

function PublicationPreviewModal({ publication, onClose, labels }) {
  if (!publication) return null;

  const journalUrl = withProtocol(publication.journalUrl);
  const previewUrl = getPublicationPreviewUrl(publication.journalUrl);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ type: 'spring', damping: 26, stiffness: 280 }}
        className="relative glass rounded-2xl max-w-6xl w-full max-h-[92vh] overflow-hidden"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full glass flex items-center justify-center text-heading hover:bg-accent-500/20 transition-colors"
          aria-label={labels.close}
        >
          <X size={20} />
        </button>

        <div className="px-6 pt-6 pb-4 border-b border-white/10 pr-16">
          <p className="text-accent-400 text-xs font-mono mb-2">{labels.previewTag}</p>
          <h3 className="text-heading text-xl font-bold mb-1">{publication.title}</h3>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <p className="text-muted">{publication.journal}</p>
            <p className="text-faint">{publication.year}</p>
          </div>

          {journalUrl && (
            <a
              href={journalUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 mt-3 text-accent-400 hover:text-accent-300 transition-colors text-sm font-medium"
            >
              {labels.openJournal}
              <ExternalLink size={14} />
            </a>
          )}
        </div>

        <div className="h-[68vh] bg-surface">
          {previewUrl ? (
            <iframe
              src={previewUrl}
              title={`${publication.title} PDF preview`}
              className="w-full h-full"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center p-6 text-center text-muted text-sm">
              {labels.unavailable}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

function AwardCard({ award, index, onPreview, labels }) {
  const thumbnail = award.proofImages?.[0];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: index * 0.1 }}
      className="glass p-5 sm:p-6 rounded-2xl border border-transparent hover:border-accent-500/30 transition-colors group"
    >
      <div className="grid grid-cols-1 gap-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-accent-500/10 flex items-center justify-center shrink-0">
            {award.highlight ? <Star className="text-accent-400" /> : <Award className="text-accent-400" />}
          </div>
          <div className="grow">
            <p className="text-faint text-sm mb-1">{award.year}</p>
            <h4 className="text-heading font-semibold mb-1.5 group-hover:text-accent-400 transition-colors">{award.title}</h4>
            <p className="text-muted text-sm font-medium mb-2">{award.issuer}</p>
            <p className="text-faint text-xs">{award.description}</p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => onPreview(award)}
          className="relative rounded-xl overflow-hidden border border-white/10 hover:border-accent-400/50 transition-colors text-left"
        >
          {thumbnail ? (
            <img
              src={thumbnail}
              alt={`${award.title} proof thumbnail`}
              className="w-full h-40 object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-40 bg-linear-to-br from-accent-500/10 to-cyan-500/10 flex items-center justify-center">
              <ImageIcon size={28} className="text-accent-400/70" />
            </div>
          )}
          <div className="absolute inset-0 bg-black/10" />
          <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-black/45 text-white text-xs font-medium">
            {labels.previewButton}
          </div>
        </button>
      </div>
    </motion.div>
  );
}

function PublicationCard({ publication, index, onPreview, labels }) {
  const journalUrl = withProtocol(publication.journalUrl);
  const hasJournalLink = Boolean(journalUrl);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: index * 0.08 }}
      className="glass p-5 sm:p-6 rounded-2xl border border-transparent hover:border-accent-500/30 transition-colors"
    >
      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 rounded-full bg-cyan-500/10 flex items-center justify-center shrink-0">
          <FileText className="text-cyan-400" />
        </div>
        <div className="grow">
          <p className="text-faint text-sm mb-1">{publication.year}</p>
          <h4 className="text-heading font-semibold mb-1.5">{publication.title}</h4>
          <p className="text-muted text-sm font-medium mb-2">{publication.journal}</p>
          {publication.authors && <p className="text-accent-400 text-xs mb-2">{publication.authors}</p>}
          <p className="text-faint text-xs">{publication.description}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => onPreview(publication)}
          disabled={!hasJournalLink}
          className="px-4 py-2 rounded-lg bg-cyan-500/15 text-cyan-300 hover:bg-cyan-500/25 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {labels.previewButton}
        </button>

        {hasJournalLink && (
          <a
            href={journalUrl}
            target="_blank"
            rel="noreferrer"
            className="px-4 py-2 rounded-lg border border-white/15 hover:border-cyan-400/60 text-muted hover:text-heading transition-colors text-sm font-medium inline-flex items-center gap-2"
          >
            {labels.openJournal}
            <ExternalLink size={14} />
          </a>
        )}
      </div>
    </motion.div>
  );
}

function PatentCard({ patent, index, labels }) {
  const referenceUrl = withProtocol(patent.referenceUrl);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: index * 0.08 }}
      className="glass p-5 sm:p-6 rounded-2xl border border-transparent hover:border-accent-500/30 transition-colors"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-accent-500/10 flex items-center justify-center shrink-0">
          <ShieldCheck className="text-accent-400" />
        </div>
        <div className="grow">
          <p className="text-faint text-sm mb-1">{patent.year}</p>
          <h4 className="text-heading font-semibold mb-1.5">{patent.title}</h4>
          <p className="text-muted text-sm font-medium mb-2">{patent.institution}</p>

          <div className="flex flex-wrap gap-2 mb-3">
            <span className="px-2.5 py-1 rounded-full text-xs bg-white/8 text-faint border border-white/10">
              {labels.number}: {patent.patentNumber}
            </span>
            <span className="px-2.5 py-1 rounded-full text-xs bg-accent-500/12 text-accent-300 border border-accent-500/30">
              {labels.status}: {patent.status}
            </span>
          </div>

          <p className="text-faint text-xs">{patent.description}</p>

          {referenceUrl && (
            <a
              href={referenceUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 mt-3 text-sm text-accent-400 hover:text-accent-300 transition-colors"
            >
              {labels.openDocument}
              <ExternalLink size={14} />
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function Education() {
  const { t } = useLanguage();
  const edu = t.education;
  const [selectedAward, setSelectedAward] = useState(null);
  const [selectedPublication, setSelectedPublication] = useState(null);

  const universityUrl = edu.universityUrl || 'https://www.unisayogya.ac.id/';

  const awardsData =
    Array.isArray(t.awards.items) && t.awards.items.length > 0
      ? t.awards.items
      : defaultAwardsData;

  const publicationsData =
    Array.isArray(edu.works?.publications) && edu.works.publications.length > 0
      ? edu.works.publications
      : defaultPublicationsData;

  const patentsData =
    Array.isArray(edu.works?.patents) && edu.works.patents.length > 0
      ? edu.works.patents
      : defaultPatentsData;

  const statsData = [
    ...(Array.isArray(edu.stats) ? edu.stats : []),
    {
      value: edu.accreditationValue || 'Unggul',
      label: edu.accreditationLabel || 'Akreditasi Kampus',
    },
  ];

  const workLabels = {
    sectionTitle: edu.works?.title || 'Publikasi Paper & Hak Paten',
    publicationsTitle: edu.works?.publicationsTitle || 'Publikasi Paper',
    patentsTitle: edu.works?.patentsTitle || 'Hak Paten',
    previewButton: edu.works?.previewButton || 'Preview PDF',
    openJournal: edu.works?.openJournal || 'Buka link jurnal',
    openDocument: edu.works?.openDocument || 'Check patent',
    number: edu.works?.patentNumber || 'No. Hak Cipta',
    status: edu.works?.patentStatus || 'Status',
    close: edu.works?.close || 'Tutup preview',
    previewTag: edu.works?.previewTag || 'Paper Preview',
    unavailable: edu.works?.previewUnavailable || 'Preview PDF belum tersedia untuk link ini. Silakan buka link jurnal langsung.',
    emptyPublications: edu.works?.emptyPublications || 'Belum ada data publikasi.',
    emptyPatents: edu.works?.emptyPatents || 'Belum ada data hak paten.',
  };

  const awardLabels = {
    proofTag: t.awards.proofTag || 'Award Proof',
    previewButton: t.awards.previewButton || 'Lihat bukti penghargaan',
    closeProof: t.awards.closeProof || 'Tutup bukti penghargaan',
    previousProof: t.awards.previousProof || 'Bukti sebelumnya',
    nextProof: t.awards.nextProof || 'Bukti selanjutnya',
    goToProof: t.awards.goToProof || 'Lihat bukti ke',
  };

  return (
    <section id="education" className="relative py-24 overflow-hidden">
      <div className="absolute top-1/2 left-0 w-96 h-96 bg-accent-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-cyan-500/5 rounded-full blur-3xl" />

      <div className="section-container relative z-10">
        {/* Section Header */}
        <AnimatedSection>
          <div className="text-center mb-16">
            <p className="text-accent-400 font-mono text-sm mb-2">{edu.subtitle}</p>
            <h2 className="text-4xl sm:text-5xl font-bold text-heading mb-4">
              {edu.title} <span className="gradient-text">{edu.titleHighlight}</span>
            </h2>
            <div className="w-24 h-1 bg-linear-to-r from-accent-500 to-cyan-500 mx-auto rounded-full" />
          </div>
        </AnimatedSection>

        <div className="max-w-6xl mx-auto space-y-10">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <AnimatedSection delay={0.2}>
                <div className="glass rounded-2xl p-8 glow-border mb-10">
                  <div className="flex flex-col sm:flex-row items-start gap-6">
                    <div className="w-16 h-16 rounded-2xl bg-linear-to-br from-accent-500 to-cyan-500 flex items-center justify-center shrink-0">
                      <GraduationCap size={32} className="text-white" />
                    </div>

                    <div className="flex-1">
                      <a
                        href={universityUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-heading text-xl font-bold mb-1 hover:text-accent-400 transition-colors"
                      >
                        {edu.university}
                        <ExternalLink size={14} />
                      </a>
                      <p className="text-accent-400 font-medium mb-3">{edu.degree}</p>

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-2">
                        {statsData.map((stat) => (
                          <div
                            key={`${stat.label}-${stat.value}`}
                            className="text-center p-3 sm:p-3.5 rounded-xl bg-surface min-h-18.5 flex flex-col items-center justify-center"
                          >
                            <div className="text-base sm:text-lg font-bold gradient-text leading-tight">{stat.value}</div>
                            <div className="text-[11px] sm:text-xs text-faint mt-1 leading-tight">{stat.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.35}>
                <h3 className="text-xl font-semibold text-heading mb-6 flex items-center gap-2">
                  <span className="w-8 h-0.5 bg-accent-500" />
                  {edu.activitiesTitle}
                </h3>

                <div className="grid sm:grid-cols-2 lg:grid-cols-1 gap-4">
                  {edu.activities.map((activity, index) => {
                    const Icon = activityIcons[index % activityIcons.length];
                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        whileHover={{ y: -3 }}
                        className="glass glass-hover rounded-xl p-5"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-lg bg-accent-500/15 flex items-center justify-center shrink-0">
                            <Icon size={18} className="text-accent-400" />
                          </div>
                          <div>
                            <h4 className="text-heading font-semibold text-sm mb-1">{activity.title}</h4>
                            <p className="text-accent-400 font-mono text-xs mb-2">{activity.period}</p>
                            <p className="text-muted text-sm leading-relaxed">{activity.desc}</p>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </AnimatedSection>
            </div>

            <div className="lg:col-span-7">
              <AnimatedSection delay={0.45}>
                <h3 className="text-xl font-semibold text-heading mb-6 flex items-center gap-2">
                  <span className="w-8 h-0.5 bg-cyan-500" />
                  {t.awards.title}
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {awardsData.map((award, index) => (
                    <AwardCard key={index} award={award} index={index} onPreview={setSelectedAward} labels={awardLabels} />
                  ))}
                </div>
              </AnimatedSection>
            </div>
          </div>

          <AnimatedSection delay={0.58}>
            <h3 className="text-xl font-semibold text-heading mb-6 flex items-center gap-2">
              <span className="w-8 h-0.5 bg-accent-500" />
              {workLabels.sectionTitle}
            </h3>

            <div className="grid lg:grid-cols-2 gap-8">
              <div className="w-full">
                <p className="text-cyan-400 font-mono text-xs mb-3">{workLabels.publicationsTitle}</p>

                <div className="grid grid-cols-1 gap-4">
                  {publicationsData.length > 0 ? (
                    publicationsData.map((publication, index) => (
                      <PublicationCard
                        key={`${publication.title}-${index}`}
                        publication={publication}
                        index={index}
                        onPreview={setSelectedPublication}
                        labels={workLabels}
                      />
                    ))
                  ) : (
                    <div className="glass p-5 rounded-2xl text-sm text-faint">{workLabels.emptyPublications}</div>
                  )}
                </div>
              </div>

              <div className="w-full">
                  <p className="text-accent-400 font-mono text-xs mb-3">{workLabels.patentsTitle}</p>

                  <div className="grid grid-cols-1 gap-4">
                    {patentsData.length > 0 ? (
                      patentsData.map((patent, index) => (
                        <PatentCard
                          key={`${patent.title}-${index}`}
                          patent={patent}
                          index={index}
                          labels={workLabels}
                        />
                      ))
                    ) : (
                      <div className="glass p-5 rounded-2xl text-sm text-faint">{workLabels.emptyPatents}</div>
                    )}
                  </div>
                </div>
              </div>
          </AnimatedSection>
          </div>
        </div>

      <AnimatePresence>
        {selectedAward && (
          <AwardProofLightbox
            award={selectedAward}
            onClose={() => setSelectedAward(null)}
            labels={awardLabels}
          />
        )}
        {selectedPublication && (
          <PublicationPreviewModal
            publication={selectedPublication}
            onClose={() => setSelectedPublication(null)}
            labels={workLabels}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
